<template>
  <div class="d3-chart-wrapper" style="position: relative;">
    <div ref="chartContainer" class="chart-container"></div>
    <div ref="tooltip" class="d3-tooltip" style="opacity: 0; pointer-events: none; position: absolute; background: rgba(0, 0, 0, 0.8); color: #fff; padding: 8px; border-radius: 4px; font-size: 12px; z-index: 10;"></div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { formatDateTime } from '@/util/date';

export default {
  name: 'FuelCharacteristics',
  props: ['series', 'type', 'deviceStatus'],
  data() {
    return {
      resizeObserver: null,
      seriesVisibility: {},
    };
  },
  mounted() {
    this.drawChart();
    this.resizeObserver = new ResizeObserver(() => this.drawChart());
    this.resizeObserver.observe(this.$el);
  },
  beforeDestroy() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  watch: {
    series: { deep: true, handler: 'drawChart' },
  },
  methods: {
    interpolateNulls(dataArray) {
      const result = [...dataArray];
      for (let i = 0; i < result.length; i++) {
        if (result[i] === null) {
          const startIndex = i - 1;
          const startValue = startIndex >= 0 ? result[startIndex] : null;

          if (startValue !== null) {
            let endIndex = -1;
            for (let j = i + 1; j < result.length; j++) {
              if (result[j] !== null) {
                endIndex = j;
                break;
              }
            }
            if (endIndex !== -1) {
              const endValue = result[endIndex];
              const gapCount = endIndex - startIndex;
              const totalDifference = endValue - startValue;
              const step = totalDifference / gapCount;
              for (let k = startIndex + 1; k < endIndex; k++) {
                const interpolatedValue = startValue + step * (k - startIndex);
                result[k] = Number(interpolatedValue.toFixed(2));
              }
              i = endIndex - 1;
            }
          }
        }
      }
      return result;
    },

    processData() {
      if (!this.series || !this.type) return null;

      const upperType = this.type.toUpperCase();
      const titleCaseType = this.type.charAt(0).toUpperCase() + this.type.slice(1).toLowerCase();

      const mapData = (path) => this.series.map((data) => {
        const keys = path.split('.');
        let val = data;
        // eslint-disable-next-line no-restricted-syntax
        for (const key of keys) val = val && val[key] ? val[key] : null;
        const num = Number(val);
        return Number.isFinite(num) ? Number(num.toFixed(2)) : null;
      });

      const inFlow = mapData(`fmData.${upperType}_InFlow`);
      const Rpm = mapData(`rpmData.${upperType}.rpm`);
      const Dens = mapData(`fmData.${upperType}_InDens`);
      const Temp = mapData(`fmData.${upperType}_IN.temperature`);

      const timestamps = this.series.map((d) => new Date(d.timestamp * 1000));

      const datasets = [];

      datasets.push({
        name: `${titleCaseType} In Flow (L/h)`, data: inFlow, interpolated: this.interpolateNulls(inFlow), color: '#0084ff', axis: 'flow',
      });

      if (this.deviceStatus && this.deviceStatus.flowmeterType === 'in-out') {
        const outFlow = mapData(`fmData.${upperType}_OutFlow`);
        datasets.push({
          name: `${titleCaseType} Out Flow (L/h)`, data: outFlow, interpolated: this.interpolateNulls(outFlow), color: '#ff0000', axis: 'flow',
        });

        const netFlow = inFlow.map((val, i) => {
          if (val !== null && outFlow[i] !== null) {
            return Number((val - outFlow[i]).toFixed(2));
          }
          return null;
        });
        datasets.push({
          name: `${titleCaseType} Net Flow (L/h)`, data: netFlow, interpolated: this.interpolateNulls(netFlow), color: '#00ff00', axis: 'flow',
        });
      }

      datasets.push({
        name: `${titleCaseType} RPM`, data: Rpm, interpolated: this.interpolateNulls(Rpm), color: '#884DFF', axis: 'rpm',
      });
      datasets.push({
        name: `${titleCaseType} Density (g/cm³)`, data: Dens, interpolated: this.interpolateNulls(Dens), color: '#10B981', axis: 'dens',
      });
      datasets.push({
        name: `${titleCaseType} Temp (°C)`, data: Temp, interpolated: this.interpolateNulls(Temp), color: '#F97316', axis: 'temp',
      });

      return { timestamps, datasets };
    },

    drawChart() {
      const processed = this.processData();
      if (!processed) return;
      const { timestamps, datasets } = processed;
      const container = this.$refs.chartContainer;
      d3.select(container).selectAll('*').remove();

      const visibility = this.seriesVisibility || {};
      datasets.forEach((dataset) => {
        if (visibility[dataset.name] === undefined) visibility[dataset.name] = true;
      });
      this.seriesVisibility = visibility;

      const visibleWidth = this.$el.clientWidth || container.clientWidth;
      const isCompact = visibleWidth < 576;
      const chartOuterWidth = Math.max(container.clientWidth, isCompact ? 900 : 0);

      const margin = {
        top: isCompact ? 56 : 40,
        right: isCompact ? 170 : 180,
        bottom: isCompact ? 48 : 50,
        left: isCompact ? 52 : 60,
      };
      const width = Math.max(1, chartOuterWidth - margin.left - margin.right);
      const chartHeight = isCompact ? 440 : 600;
      const height = chartHeight - margin.top - margin.bottom;
      const xTickCount = isCompact ? 5 : 8;
      const yTickCount = isCompact ? 5 : 10;
      const densAxisOffset = isCompact ? 56 : 60;
      const tempAxisOffset = isCompact ? 112 : 120;

      const svg = d3.select(container).append('svg')
        .attr('width', chartOuterWidth)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      const x = d3.scaleTime().domain(d3.extent(timestamps)).range([0, width]);
      svg.append('g').attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).ticks(xTickCount).tickFormat((d) => formatDateTime(d, 'DD/MM HH:mm')))
        .selectAll('text')
        .style('text-anchor', 'end')
        .attr('dx', '-.8em')
        .attr('dy', '.15em')
        .attr('transform', 'rotate(-45)');

      // Scales
      const flowDatasets = datasets.filter((d) => d.axis === 'flow');
      const maxFlow = d3.max(flowDatasets.flatMap((d) => d.interpolated)) || 10;
      const yFlow = d3.scaleLinear().domain([0, maxFlow * 1.1]).range([height, 0]);

      svg.append('g').call(d3.axisLeft(yFlow).ticks(yTickCount))
        .append('text').attr('fill', '#000')
        .attr('transform', 'rotate(-90)')
        .attr('y', isCompact ? -36 : -40)
        .attr('dy', '0.71em')
        .style('text-anchor', 'end')
        .text('Flow (L/h)');

      const rpmDataset = datasets.find((d) => d.axis === 'rpm');
      const maxRpm = d3.max(rpmDataset.interpolated) || 1000;
      const yRpm = d3.scaleLinear().domain([0, (Math.ceil(maxRpm / 100) * 100) + 100]).range([height, 0]);
      svg.append('g').attr('transform', `translate(${width}, 0)`).call(d3.axisRight(yRpm).ticks(yTickCount))
        .append('text')
        .attr('fill', rpmDataset.color)
        .attr('transform', 'rotate(-90)')
        .attr('y', 30)
        .attr('dy', '0.71em')
        .style('text-anchor', 'end')
        .text(rpmDataset.name);

      const densDataset = datasets.find((d) => d.axis === 'dens');
      const maxDens = d3.max(densDataset.interpolated) || 1;
      const yDens = d3.scaleLinear().domain([0, maxDens * 1.1]).range([height, 0]);
      svg.append('g').attr('transform', `translate(${width + densAxisOffset}, 0)`).call(d3.axisRight(yDens).ticks(yTickCount))
        .append('text')
        .attr('fill', densDataset.color)
        .attr('transform', 'rotate(-90)')
        .attr('y', 30)
        .attr('dy', '0.71em')
        .style('text-anchor', 'end')
        .text(densDataset.name);

      const tempDataset = datasets.find((d) => d.axis === 'temp');
      const maxTemp = d3.max(tempDataset.interpolated) || 100;
      const yTemp = d3.scaleLinear().domain([0, maxTemp * 1.1]).range([height, 0]);
      svg.append('g').attr('transform', `translate(${width + tempAxisOffset}, 0)`).call(d3.axisRight(yTemp).ticks(yTickCount))
        .append('text')
        .attr('fill', tempDataset.color)
        .attr('transform', 'rotate(-90)')
        .attr('y', 30)
        .attr('dy', '0.71em')
        .style('text-anchor', 'end')
        .text(tempDataset.name);

      svg.append('g').attr('class', 'grid').attr('opacity', 0.1).call(d3.axisLeft(yFlow).ticks(yTickCount).tickSize(-width).tickFormat(''));

      const getScale = (axisName) => {
        if (axisName === 'flow') return yFlow;
        if (axisName === 'rpm') return yRpm;
        if (axisName === 'dens') return yDens;
        return yTemp;
      };

      const seriesElements = {};
      const realLineWidth = 2.5;
      const hoverLineWidth = 4;
      const interpolatedLineWidth = 1.5;
      datasets.forEach((series, index) => {
        const yScale = getScale(series.axis);
        // Line Interpolated
        const interpolatedLine = svg.append('path')
          .datum(series.interpolated)
          .attr('fill', 'none').attr('stroke', series.color)
          .attr('stroke-width', interpolatedLineWidth)
          .attr('stroke-dasharray', '4 4')
          .attr('opacity', 0.5)
          .attr('d', d3.line().x((d, i) => x(timestamps[i])).y((d) => yScale(d)));

        // Line Real
        const realLine = svg.append('path')
          .datum(series.data)
          .attr('fill', 'none').attr('stroke', series.color)
          .attr('stroke-width', realLineWidth)
          .attr('d', d3.line().defined((d) => d !== null).x((d, i) => x(timestamps[i])).y((d) => yScale(d)))
          .on('mouseover', function handleMouseOver() {
            d3.select(this).attr('stroke-width', hoverLineWidth);
          })
          .on('mouseout', function handleMouseOut() {
            d3.select(this).attr('stroke-width', realLineWidth);
          });

        // Dots
        const dots = svg.selectAll(`.dot-series-${index}`)
          .data(series.data.map((d, i) => ({ value: d, date: timestamps[i] })).filter((d) => d.value !== null))
          .enter()
          .append('circle')
          .attr('class', `dot-series-${index}`)
          .attr('cx', (d) => x(d.date))
          .attr('cy', (d) => yScale(d.value))
          .attr('r', 3)
          .attr('fill', series.color)
          .style('pointer-events', 'none');

        seriesElements[series.name] = {
          interpolatedLine,
          realLine,
          dots,
        };
      });

      // Tooltip
      const tooltip = d3.select(this.$refs.tooltip);
      const bisect = d3.bisector((d) => d).left;

      const focusGroup = svg.append('g').style('display', 'none');
      const focusLine = focusGroup.append('line').attr('stroke', '#aaa').attr('stroke-dasharray', '3 3').attr('y1', 0)
        .attr('y2', height);
      const focusCircles = datasets.map((ds) => focusGroup.append('circle').attr('r', 6).attr('fill', ds.color).attr('stroke', '#fff')
        .attr('stroke-width', 2));

      const updateSeriesVisibility = () => {
        datasets.forEach((ds, index) => {
          const isVisible = visibility[ds.name];
          const elements = seriesElements[ds.name];
          elements.interpolatedLine.style('display', isVisible ? null : 'none');
          elements.realLine.style('display', isVisible ? null : 'none');
          elements.dots.style('display', isVisible ? null : 'none');
          focusCircles[index].style('display', isVisible ? null : 'none');
        });
      };

      const legendSpacing = 18;
      const legendItemWidth = 160;
      const legendColumns = Math.max(1, Math.floor(width / legendItemWidth));
      const legendWidth = Math.min(datasets.length, legendColumns) * legendItemWidth;
      const legendGroup = svg.append('g')
        .attr('class', 'chart-legend')
        .attr('transform', `translate(${Math.max(0, (width - legendWidth) / 2)}, -25)`);
      const legendItem = legendGroup.selectAll('g')
        .data(datasets)
        .enter()
        .append('g')
        .attr('transform', (d, i) => {
          const col = i % legendColumns;
          const row = Math.floor(i / legendColumns);
          return `translate(${col * legendItemWidth}, ${row * legendSpacing})`;
        })
        .style('cursor', 'pointer')
        .on('click', (event, d) => {
          visibility[d.name] = !visibility[d.name];
          updateSeriesVisibility();
          // eslint-disable-next-line no-use-before-define
          updateLegend();
        });

      legendItem.append('rect')
        .attr('width', 12)
        .attr('height', 12)
        .attr('y', -10)
        .attr('rx', 2)
        .attr('ry', 2)
        .attr('fill', (d) => d.color);

      legendItem.append('text')
        .attr('x', 18)
        .attr('y', 0)
        .attr('dy', '0.32em')
        .style('font-size', '12px')
        .text((d) => d.name);

      const updateLegend = () => {
        legendItem.select('rect').attr('opacity', (d) => (visibility[d.name] ? 1 : 0.3));
        legendItem.select('text')
          .attr('opacity', (d) => (visibility[d.name] ? 1 : 0.4))
          .style('text-decoration', (d) => (visibility[d.name] ? 'none' : 'line-through'));
      };

      updateSeriesVisibility();
      updateLegend();

      svg.append('rect')
        .attr('width', width).attr('height', height).style('fill', 'none')
        .style('pointer-events', 'all')
        .on('mouseover', () => { tooltip.style('opacity', 1); focusGroup.style('display', null); })
        .on('mouseout', () => { tooltip.style('opacity', 0); focusGroup.style('display', 'none'); })
        .on('mousemove', (event) => {
          const x0 = x.invert(d3.pointer(event)[0]);
          const i = bisect(timestamps, x0, 1);
          // eslint-disable-next-line no-nested-ternary
          const index = i < timestamps.length && i > 0 ? (x0 - timestamps[i - 1] > timestamps[i] - x0 ? i : i - 1) : 0;
          const date = timestamps[index];
          const xPos = x(date);

          focusLine.attr('x1', xPos).attr('x2', xPos);

          datasets.forEach((ds, i) => {
            if (!visibility[ds.name]) {
              focusCircles[i].style('display', 'none');
              return;
            }
            const val = ds.data[index];
            const yScale = getScale(ds.axis);
            if (val !== null && val !== undefined) {
              focusCircles[i].attr('cx', xPos).attr('cy', yScale(val)).style('display', null);
            } else {
              focusCircles[i].style('display', 'none');
            }
          });

          let html = `<strong>${formatDateTime(date, 'DD MMM HH:mm')}</strong><br/>`;
          datasets.forEach((ds) => {
            if (!visibility[ds.name]) return;
            const val = ds.data[index];
            if (val !== null && val !== undefined) html += `<div style="display:flex;align-items:center;gap:5px;"><span style="width:8px;height:8px;border-radius:50%;background:${ds.color};"></span>${ds.name}: ${val}</div>`;
          });

          const [posX, posY] = d3.pointer(event, container);
          let left = posX + 15;
          const top = posY + 15;
          if (left + 160 > chartOuterWidth) left = posX - 170;
          tooltip.html(html).style('left', `${left}px`).style('top', `${top}px`);
        });
    },
  },
};
</script>

<style scoped>
.d3-chart-wrapper { font-family: sans-serif; }

@media screen and (max-width: 575px) {
  .d3-chart-wrapper {
    min-width: 900px;
  }
}
</style>
